import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from 'enrollment/app/routes';
import {
  BenefitDto,
  CategoryCoverageDto,
  DependentDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import { DeclineMessage } from '../DeclineMessage';
import { CoverageAmount } from '../CoverageAmounts';
import { Covered } from '../Covered';
import { getPremiumStyled, RETURN_VALUE_ONLY } from 'enrollment/utils';
import { selectPerPayPeriodText } from 'enrollment/services';
import { AlertBox } from 'components';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { PlanBodyStyled } from './PlanStyled';
import {
  RiderBenefitDrawer,
  DRAWER_ON_REVIEW,
} from 'enrollment/components/RiderBenefits';
import {
  NEW_HIRE_ENROLLMENT_TYPE,
  useCompanySettingsQuery,
  useConfigurationQuery,
} from 'theSource/queries';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { HsaPlanDetails } from './HsaPlanDetails';
import {
  getDrawerRiders,
  showRiderDetails as showRiderDetailsFunc,
} from 'enrollment/features/Benefits/BenefitsPreview/GroupedBenefits/utils';
import { DefinedBenefitContributionDrawer } from 'enrollment/components';
import { PsButtonTuner } from 'stencilSetup';
import { queryClient } from 'theSource/utils';
import { GET_BENEFIT_PLANS_QUERY_KEY } from 'enrollment/queries/useBenefitPlansQuery/utils';

interface PlanProps {
  planName: string;
  provider?: string;
  inEligible?: boolean;
  cost?: string;
  subscriberCost?: string;
  planId: string;
  declined?: boolean;
  coveredIndividuals?: DependentDto[];
  coverageAmt?: string;
  guaranteedAmount?: number;
  isDependentOnlyPlan: boolean;
  contextLookup?: string;
  benefitData?: BenefitDto;
  orgBenefitId: number;
  benefitCategory: string;
  benefitClass: string;
  waitingForEoi?: boolean;
  waitingForDocRequest?: boolean;
  canEdit?: boolean;
  currentCategory?: string;
  onMount?: (id: string) => void;
  planRequiresConfirmation?: boolean;
}

export const Plan: React.FC<PlanProps> = ({
  planName,
  provider,
  inEligible,
  cost,
  subscriberCost,
  planId,
  declined,
  coveredIndividuals,
  coverageAmt,
  guaranteedAmount,
  isDependentOnlyPlan,
  contextLookup,
  benefitData,
  orgBenefitId,
  benefitCategory,
  benefitClass,
  waitingForEoi = false,
  waitingForDocRequest = false,
  canEdit = true,
  currentCategory,
  onMount,
  planRequiresConfirmation = false,
}) => {
  const { t } = useTranslation('pages.enrollment.review');
  const navigate = useNavigate();
  const isMobile = window.innerWidth < 744;
  const ref = useRef<HTMLDivElement>(null);
  const perPayText = useSelector(selectPerPayPeriodText);
  const isCoveragePending = waitingForEoi || waitingForDocRequest;

  const reqVolume = Number(coverageAmt) || 0;
  const actualAmount = Number(guaranteedAmount) || 0;
  const reqPremium = Number(cost) || 0;
  const actualPremium = Number(subscriberCost) || 0;
  const hideEmptyCost = benefitData?.benefit.hide_cost_if_empty;

  const coverage = benefitData?.coverage;
  const orgPlanYear = benefitData?.benefit.org_plan_year_id || 0;
  const showRiderDetails = showRiderDetailsFunc(
    coverage as CategoryCoverageDto
  );

  const drawerRiders = getDrawerRiders(coverage as CategoryCoverageDto);

  const isHsaBenefit = benefitData?.benefit.benefit_lookup_code === 'hsa';

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(lumosApiHost),
  });
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  const isHSANewExperience =
    companySettings?.data?.hsa_new_experience_feature_toggle &&
    isHsaBenefit &&
    enrollmentContextType === NEW_HIRE_ENROLLMENT_TYPE;

  const displayWarningBanner =
    isHSANewExperience && coverage?.is_subscriber_fsa_contribution;

  const reqAmount = getPremiumStyled({
    premium: String(reqVolume),
    valueOnly: RETURN_VALUE_ONLY,
  });
  const differenceAmount = getPremiumStyled({
    premium: String(reqVolume - actualAmount),
    valueOnly: RETURN_VALUE_ONLY,
  });
  const differencePremium = getPremiumStyled({
    premium: String(reqPremium - actualPremium),
    valueOnly: RETURN_VALUE_ONLY,
  });

  let alertText = '';
  if (waitingForEoi) {
    alertText = t('alertEoiReview', {
      reqAmount: reqAmount,
      differenceAmount: differenceAmount,
      differencePremium: differencePremium,
    });
  } else if (waitingForDocRequest) {
    alertText = t('alertDocRequestReview', {
      differencePremium: differencePremium,
    });
  }

  const stateProps = {
    benefitData: benefitData,
    currentBenefitCategory: benefitCategory,
    currentCategory: currentCategory,
    contextLookup: contextLookup,
    fromReview: window.location.pathname.includes(
      ROUTES.enrollment.review.path
    ),
  };

  const coverageAmountProps = {
    amount: coverageAmt || '0',
    stackedCoverageLines: coverage?.coverage_lines || undefined,
  };

  const showCoverageAmount =
    !benefitData?.benefit.hide_volume_on_confirm &&
    ((!declined && coverageAmt && !waitingForEoi) ||
      (!declined && guaranteedAmount && waitingForEoi));

const lookupCode = benefitData?.benefit.benefit_lookup_code;
  useEffect(() => {
    
    if (ref.current && onMount && lookupCode) {
      onMount(lookupCode);
    }
  }, [onMount, lookupCode]);

  const handleChangePlan = () => {
    //used for benefit plans. this invalidates all plan queries matching this including those with and without dependents - needed if dependent plan viewed out of order
    queryClient.invalidateQueries({
      queryKey: [GET_BENEFIT_PLANS_QUERY_KEY + '-' + orgBenefitId],
    });
    if (benefitClass === 'survey') {
      navigate(`/enrollment${ROUTES.enrollment.survey.path}/${orgBenefitId}`, {
        state: {
          ...stateProps,
          benefitId: orgBenefitId,
        },
      });
    } else {
      navigate(`/enrollment${ROUTES.enrollment.plans.path}/${orgBenefitId}`, {
        state: {
          ...stateProps,
        },
      });
    }
  };


  return (
    <React.Fragment>
      <div className="plan" id={lookupCode} ref={ref}>
        <div className="plan-header">
          <div
            className={provider ? 'mr-md-auto' : 'd-flex align-items-center'}
          >
            <h3
              className="align-self-center"
              id={benefitData?.benefit.benefit_lookup_code}
            >
              <span className="sr-only">{t('planName')}</span> {planName}
            </h3>
            {!inEligible &&
              !declined &&
              provider &&
              provider !== 'undefined' && (
                <div className="mb-2_5">
                  <span className="sr-only">{t('provider')}</span>
                  {provider}
                </div>
              )}
          </div>
          <div className="button-container d-none d-lg-inline-block">
            {canEdit && (
              <PsButtonTuner
                label={planRequiresConfirmation ? t('explore') : t('change')}
                level={planRequiresConfirmation ? 'primary' : 'secondary'}
                buttonId={`change_plan_${planId}`}
                onClick={handleChangePlan}
                otherData={[
                  {
                    attribute: 'aria-describedby',
                    value: lookupCode || '',
                  },
                ]}
              />
            )}
          </div>
          {declined ? (
            <div
              className="d-none d-lg-inline"
              data-testid="headerDeclinedMessage"
            >
              <DeclineMessage />
            </div>
          ) : (
            <div>
              <div className="cost" data-testid="costWrapper">
                {!inEligible && cost ? (
                  <div style={{ minWidth: '90px' }}>
                    {(!hideEmptyCost || Number(cost) > 0) && (
                      <>
                        {getPremiumStyled({
                          premium: cost,
                          valueOnly: RETURN_VALUE_ONLY,
                        })}
                        <div className="d-none d-md-block per-pay">{`${perPayText}`}</div>
                      </>
                    )}
                  </div>
                ) : (
                  'Unelected'
                )}
              </div>
              {!inEligible && cost && showRiderDetails && isMobile && (
                <div className="d-flex justify-content-end pr-0 mr-n3">
                  <RiderBenefitDrawer
                    riders={drawerRiders}
                    orgPlanYearId={orgPlanYear}
                    drawerOrigin={DRAWER_ON_REVIEW}
                  />
                </div>
              )}
              {coverage?.defined_benefit_credits &&
                coverage?.defined_benefit_credits.length > 0 &&
                isMobile && (
                  <DefinedBenefitContributionDrawer
                    btnName={t(
                      'enrollment.benefits:definedBenefitContribution.reviewCreditsApplied'
                    )}
                    category={currentCategory || benefitCategory}
                  />
                )}
            </div>
          )}
        </div>
        {declined ? (
          <div
            className="d-block d-md-inline-block d-lg-none"
            data-testid="bodyDeclinedMessage"
          >
            <DeclineMessage />
          </div>
        ) : (
          ' '
        )}
        {!inEligible && isHSANewExperience && (
          <HsaPlanDetails coverage={coverage}></HsaPlanDetails>
        )}
        {!inEligible && cost ? (
          <PlanBodyStyled className="review-planbody">
            <Covered
              individuals={coveredIndividuals}
              isDeclinePlan={declined}
              isDependentOnlyPlan={isDependentOnlyPlan}
              benefitCategory={benefitCategory}
            />
            <div className="d-flex justify-content-end w-50">
              {showRiderDetails && !isMobile && (
                <RiderBenefitDrawer
                  riders={drawerRiders}
                  orgPlanYearId={orgPlanYear}
                  drawerOrigin={DRAWER_ON_REVIEW}
                />
              )}
              {coverage?.defined_benefit_credits &&
                coverage?.defined_benefit_credits.length > 0 &&
                coverage.req_coverage_level_lookup_code !== 'decline' &&
                !isMobile && (
                  <DefinedBenefitContributionDrawer
                    btnName={t(
                      'enrollment.benefits:definedBenefitContribution.reviewCreditsApplied'
                    )}
                    category={currentCategory || benefitCategory}
                  />
                )}
            </div>
          </PlanBodyStyled>
        ) : (
          <div />
        )}

        {/* and there are covered individuals - some policies don't specify anyone - needs updated */}
        {!inEligible && showCoverageAmount && (
          <CoverageAmount {...coverageAmountProps} />
        )}
        <div className="button-container d-lg-none">
          {canEdit && (
            <ps-button
              level="secondary"
              label={t('change')}
              id={`change_plan_${planId}`}
              onClick={handleChangePlan}
            ></ps-button>
          )}
        </div>

        {!inEligible && displayWarningBanner && (
          <div className="mt-3">
            <PSBannerWrapper
              id="hsa-warning-message"
              message={t('HSABenefitCardWarningMessage')}
              level="warning"
              icon
            />
          </div>
        )}

        {isCoveragePending && (
          <div className="mt-3">
            <AlertBox innerText={alertText} />
          </div>
        )}

        {planRequiresConfirmation && (
          <div className="mt-3">
            <PSBannerWrapper
              id="copied-elections-banner"
              message={t('enrollment.benefits:preElectedElectionsBanner')}
              level="info"
              icon
            />
          </div>
        )}
      </div>
    </React.Fragment>
  );
};
